import React, { useEffect, useState } from 'react';
import { Empty } from 'antd';
import { useNavigate } from 'react-router-dom';
import { formatNumber } from '../utils/numberUtil';
import axiosInstance from '../utils/axiosInstance';
import { runSafely } from '../utils/runSafely';
import Loader from '../Components/Loader';
import { useTranslation } from 'react-i18next';
import AppShell from '../Components/AppShell';
import Container from '../Components/Container';

interface DetailType {
    key: React.Key;
    id: number;
    name: string;
    price: number;
    commission: number;
    status: "pending" | "completed",
    referenceNo: string;
    url: string;
    productType: string;
}

/** The four filters, in display order. Declared as data so the tab row is one map. */
const FILTERS: { key: "all" | "pending" | "completed" | "on-hold"; labelKey: string }[] = [
    { key: 'all', labelKey: 'all' },
    { key: 'pending', labelKey: 'pending' },
    { key: 'completed', labelKey: 'completed' },
    { key: 'on-hold', labelKey: 'on_hold' },
];

/**
 * Product thumbnail.
 *
 * `object-cover` is the fix for a real bug: the image was stretched to fill a
 * 50x65 box with no aspect handling, so every product photo rendered as a
 * distorted smear. Applies at both breakpoints, because it was wrong at both.
 */
const Thumb: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
    <div className='w-16 h-20 shrink-0 overflow-hidden border border-solid border-brand-300 bg-brand-50'>
        <img className='w-full h-full object-cover' src={src} alt={alt} />
    </div>
);

/**
 * Status as a muted dot plus label, replacing Ant Design's filled `Tag`.
 * The default chips read as generic admin UI against this palette; a dot
 * carries the same information without competing with the product name.
 */
const StatusMark: React.FC<{ status: DetailType['status']; label: string }> = ({ status, label }) => (
    <span className='inline-flex items-center gap-2 whitespace-nowrap'>
        <span
            aria-hidden='true'
            className={
                'inline-block w-1.5 h-1.5 rounded-full ' +
                (status === 'completed' ? 'bg-success' : 'bg-accent-500')
            }
        />
        <span className='text-sm text-brand-600'>{label}</span>
    </span>
);

/** Product type as a hairline outlined pill — no fill, so it stays quiet. */
const TypePill: React.FC<{ type: string }> = ({ type }) => (
    <span className='inline-block border border-solid border-brand-300 rounded-full px-2.5 py-0.5 text-xs uppercase tracking-[0.14em] text-brand-600'>
        {type}
    </span>
);

const DetailPage: React.FC = () => {
    const [loading, setLoading] = useState(false);

    const [selectedFilter, setSelectedFilter] = useState<"all" | "pending" | "completed" | "on-hold">("all");
    const [histories, setHistories] = useState<DetailType[]>([]);

    const navigate = useNavigate();
    const { t } = useTranslation();

    const getOrderHistory = async () => {
        setLoading(true);
        const res = await axiosInstance.get("/protected/getUserInfo")
        const data = res.data.data;
        if (data) {
            const user = data.user;
            if (!user.isActive) {
                navigate("/login");
                return;
            }

            const userOrders = data.userOrders;

            setHistories(userOrders?.map((e: any) => {
                return {
                    id: e.id,
                    key: e.id,
                    name: e.Product?.name,
                    price: e.totalAmount,
                    commission: e.comission,
                    referenceNo: e.referenceNo,
                    status: e.orderStatus,
                    url: process.env.REACT_APP_FOLDER_URL + "/" + e.Product?.img,
                    productType: e.productType,
                }
            }) || [])
        } else {
            navigate("/login")
        }
        setTimeout(() => {
            setLoading(false);
        }, 1500);

    }

    // Mount-only fetch; see AccountPage for why the dependency is omitted.
    useEffect(() => {
        runSafely('DetailPage.getOrderHistory', getOrderHistory, () => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Same predicate as before, computed once and shared by both the card list
    // and the table so the two presentations never drift out of sync.
    const filteredHistories = histories.filter(e => {
        if (selectedFilter === 'all') return true;
        if (selectedFilter === 'completed') return e.status === 'completed';
        if (selectedFilter === 'pending') return e.status === 'pending';
        return false; // Fallback for unexpected filter values
    });

    const openIfPending = (e: DetailType) => {
        if (e.status === 'pending') {
            navigate("/confirmation");
        }
    };

    return (
        <AppShell>
            {loading && <Loader />}
            <Container>
                <div className='p-6 lg:py-14 flex flex-col gap-2'>
                    {/*
                      Tabs are real <button>s rather than clickable <div>s, so they
                      are keyboard reachable and focusable — same handlers, same
                      targets. This matches the treatment already applied to the
                      Account service tiles.
                    */}
                    <div className='flex gap-2 justify-between lg:justify-start lg:gap-10 border-0 border-b border-solid border-brand-300'>
                        {FILTERS.map(({ key, labelKey }) => (
                            <button
                                key={key}
                                type='button'
                                onClick={() => setSelectedFilter(key)}
                                className={
                                    'bg-transparent border-0 p-0 pb-3 -mb-px cursor-pointer ' +
                                    'text-sm uppercase tracking-[0.16em] transition-colors duration-150 ' +
                                    'border-0 border-b-2 border-solid ' +
                                    (selectedFilter === key
                                        ? 'text-brand-900 border-accent-500'
                                        : 'text-brand-600 border-transparent hover:text-brand-900')
                                }
                            >
                                {t(labelKey)}
                            </button>
                        ))}
                    </div>

                    {selectedFilter === "on-hold" ? <Empty className='mt-10' /> : <>
                        <div className='mt-10 flex flex-col gap-4 lg:hidden'>
                            {filteredHistories.map(e => {
                                return <div className={'flex flex-col gap-4 bg-white p-2 rounded-[1rem] ' + (e.status === "pending" ? "cursor-pointer" : "")} key={e.key}
                                    onClick={() => openIfPending(e)}>
                                    <div className='flex justify-between'>
                                        <div>{t("mall")}</div>
                                        <div><StatusMark status={e.status} label={t(e.status)} /></div>
                                    </div>
                                    <div className='border border-solid border-brand-300'></div>
                                    <div className='flex gap-4'>
                                        <Thumb src={e.url} alt={e.name} />
                                        <div className='flex-1'>
                                            <div className='flex justify-between gap-2'>
                                                <div>{e.name}</div>
                                                <TypePill type={e.productType} />
                                            </div>
                                            <div className='flex justify-between'>
                                                <div className='text-[14px] text-brand-600 my-auto'>{t("price")}:</div>
                                                <div className='text-[16px] font-display text-brand-900 my-auto'>RM {formatNumber(e.price)}</div>
                                            </div>
                                            <div className='flex justify-between'>
                                                <div className='text-[14px] text-brand-600 my-auto'>{t("reward")}:</div>
                                                <div className='text-[16px] font-display text-accent-600 my-auto'>RM {formatNumber(e.commission)}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='border border-solid border-brand-300'></div>
                                    <div className='text-brand-600 text-[14px]'>
                                        {t("orderNo")}: {e.referenceNo}
                                    </div>
                                </div>
                            })}

                            {!filteredHistories.length && <Empty className='mt-10' />
                            }
                        </div>

                        <div className='hidden lg:block mt-12'>
                            <table className='w-full text-left border-collapse'>
                                <thead>
                                    <tr className='text-brand-600'>
                                        <th className='p-0 pb-4 font-normal'></th>
                                        <th className='p-0 pb-4 pl-6 font-normal text-xs uppercase tracking-[0.16em]'>{t("mall")}</th>
                                        <th className='p-0 pb-4 pl-6 font-normal text-xs uppercase tracking-[0.16em]'>{t("price")}</th>
                                        <th className='p-0 pb-4 pl-6 font-normal text-xs uppercase tracking-[0.16em]'>{t("reward")}</th>
                                        <th className='p-0 pb-4 pl-6 font-normal text-xs uppercase tracking-[0.16em]'>{t("status")}</th>
                                        <th className='p-0 pb-4 pl-6 font-normal text-xs uppercase tracking-[0.16em]'>{t("orderNo")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredHistories.map(e => {
                                        return <tr
                                            className={
                                                'border-0 border-t border-solid border-brand-300 transition-colors duration-150 ' +
                                                'hover:bg-brand-100 ' +
                                                (e.status === "pending" ? "cursor-pointer" : "")
                                            }
                                            key={e.key}
                                            onClick={() => openIfPending(e)}>
                                            <td className='py-5'><Thumb src={e.url} alt={e.name} /></td>
                                            <td className='py-5 pl-6'>
                                                <div className='text-brand-900'>{e.name}</div>
                                                <div className='mt-2'><TypePill type={e.productType} /></div>
                                            </td>
                                            <td className='py-5 pl-6 font-display text-lg text-brand-900 whitespace-nowrap'>RM {formatNumber(e.price)}</td>
                                            <td className='py-5 pl-6 font-display text-lg text-accent-600 whitespace-nowrap'>RM {formatNumber(e.commission)}</td>
                                            <td className='py-5 pl-6'><StatusMark status={e.status} label={t(e.status)} /></td>
                                            <td className='py-5 pl-6 text-sm text-brand-600'>{e.referenceNo}</td>
                                        </tr>
                                    })}
                                </tbody>
                            </table>
                            {!filteredHistories.length && <Empty className='mt-10' />}
                        </div>
                    </>}
                </div>
            </Container>
        </AppShell>
    );
}

export default DetailPage;
